import { GS, concatBytes } from './commands';

/**
 * ESC/POS GS v 0 raster bit image (normal density).
 * @see https://reference.epson-biz.com/modules/ref_escpos/index.html
 */
export function cmdRasterBitImage(rasterRows: Uint8Array, bytesPerRow: number, heightPx: number): Uint8Array {
  const xL = bytesPerRow & 0xff;
  const xH = (bytesPerRow >> 8) & 0xff;
  const yL = heightPx & 0xff;
  const yH = (heightPx >> 8) & 0xff;
  const header = new Uint8Array([GS, 0x76, 0x30, 0x00, xL, xH, yL, yH]);
  return concatBytes(header, rasterRows);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('thermalReceipt.errors.logoLoadFailed'));
    img.src = url;
  });
}

export type LogoRaster1Bit = {
  rows: Uint8Array;
  bytesPerRow: number;
  heightPx: number;
};

/**
 * Fetch logo, scale to paper width (centered on canvas), convert to 1-bit raster rows.
 */
export async function encodeLogoRaster1Bit(
  imageUrl: string,
  dotsPerLine: number,
): Promise<LogoRaster1Bit | null> {
  if (typeof document === 'undefined' || !imageUrl.trim()) return null;

  try {
    const img = await loadImage(imageUrl);
    const maxLogoW = Math.floor(dotsPerLine * 0.72);
    const maxLogoH = Math.floor(dotsPerLine * 0.22);
    const scale = Math.min(maxLogoW / img.naturalWidth, maxLogoH / img.naturalHeight, 1);
    const drawW = Math.max(1, Math.round(img.naturalWidth * scale));
    const drawH = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = dotsPerLine;
    canvas.height = drawH;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, dotsPerLine, drawH);
    const xOff = Math.floor((dotsPerLine - drawW) / 2);
    ctx.drawImage(img, xOff, 0, drawW, drawH);

    const { data, width, height } = ctx.getImageData(0, 0, dotsPerLine, drawH);
    const bytesPerRow = Math.ceil(width / 8);
    const rows = new Uint8Array(bytesPerRow * height);

    for (let y = 0; y < height; y += 1) {
      for (let xb = 0; xb < bytesPerRow; xb += 1) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit += 1) {
          const x = xb * 8 + bit;
          if (x >= width) continue;
          const i = (y * width + x) * 4;
          const lum = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
          const alpha = data[i + 3]!;
          const isBlack = alpha > 64 && lum < 175;
          if (isBlack) byte |= 0x80 >> bit;
        }
        rows[y * bytesPerRow + xb] = byte;
      }
    }

    return { rows, bytesPerRow, heightPx: height };
  } catch {
    return null;
  }
}

/** ESC/POS GS v 0 wrapper around {@link encodeLogoRaster1Bit}. */
export async function encodeLogoRasterEscPos(
  imageUrl: string,
  dotsPerLine: number,
): Promise<Uint8Array | null> {
  const raster = await encodeLogoRaster1Bit(imageUrl, dotsPerLine);
  if (!raster) return null;
  return cmdRasterBitImage(raster.rows, raster.bytesPerRow, raster.heightPx);
}
